import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchKeywordSets, fetchPsap } from '@rsos/reduxDataStore';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { fetchUser } from '@rsos/sinatra';
import { type AdminState } from '../../../reducers';
import { loggedInSelector } from '../state/loggedInSelector';

export const initialize = createAsyncThunk<void, void, { state: AdminState }>(
  'keywordManagement/initialize',
  async (_, { dispatch, getState }) => {
    try {
      await dispatch(fetchUser());
      dispatch(fetchKeywordSets());

      // KeywordManagement doesn't read the PSAP itself, but the sidebar and this
      // section's route guard both resolve org capabilities from it, and an empty
      // capability set reads as "not entitled". So this has to be awaited: opening
      // the section's gate while it is still in flight bounces the user off a page
      // they are entitled to.
      const orgName = organizationSelector(getState())?.name;
      if (!orgName) {
        throw new Error('No admin organization for the current role');
      }
      await dispatch(fetchPsap(`/psaps/${orgName}`)).unwrap();
    } catch (err) {
      if (loggedInSelector(getState())) {
        throw err;
      } else {
        window.location.href = '/';
      }
    }
  },
);

export type InitializationState = {
  initializing: boolean;
  initialized: boolean;
  error: null | string;
};

const initialState: InitializationState = {
  initializing: true,
  initialized: false,
  error: null,
};

const initializationSlice = createSlice({
  name: 'initialization',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initialize.pending, state => {
        state.initializing = true;
        state.initialized = false;
        state.error = null;
      })
      .addCase(initialize.fulfilled, state => {
        state.initializing = false;
        state.initialized = true;
        state.error = null;
      })
      .addCase(initialize.rejected, (state, { error = {} }) => {
        state.initializing = false;
        state.initialized = false;
        state.error = error.message || 'Unknown error';
      });
  },
});

export const initializationReducer = initializationSlice.reducer;
export const initializationSelector = (state: AdminState) =>
  state.initialization;
